import { getPresignedUrl } from '@/app/lib/actions/s3';

/**
 * Check if a URL is an S3 key (vs. a full URL)
 * @param url The URL to check
 * @returns True if it's an S3 key, false if it's a full URL
 */
export function isS3Key(url: string): boolean {
  return !url.startsWith('https://') && !url.startsWith('http://');
}


export const extractPublicId = (imageUri: string): string => {
  if (!imageUri) return '';
  // Find the last dot and remove everything from that point onwards
  const lastDotIndex = imageUri.lastIndexOf('.');
  return lastDotIndex !== -1 ? imageUri.substring(0, lastDotIndex) : imageUri;
};

/**
 * Extract S3 key from image URI (handles both S3 keys and URLs containing S3 keys)
 * @param imageUri The image URI to process
 * @returns The extracted S3 key or the original imageUri if it's already a key
 */
export function extractS3KeyFromImageUri(imageUri: string): string {
  if (!imageUri) return '';
  
  // Check if this is a URL that contains an S3 key path
  // Example: https://dangochat.com/chat/rita-swap-images/u-vSOjV52Fssi/a-1Xzeja56CfF/test_swap.jpg
  // Should extract: rita-swap-images/u-vSOjV52Fssi/a-1Xzeja56CfF/test_swap.jpg
  if (imageUri.includes('/chat/') && (imageUri.includes('rita-swap-images/') || imageUri.includes('rita-avatars/'))) {
    const s3Key = imageUri.split('/chat/')[1];
    return s3Key;
  }
  
  // If it's already an S3 key (not a full URL), return as-is
  if (isS3Key(imageUri)) {
    return imageUri;
  }
  
  // If it's a full URL but not our expected format, return empty string
  return '';
}

/**
 * Load image from presigned URL and create an HTMLImageElement
 * @param presignedUrl The presigned URL to load the image from
 * @returns Promise that resolves to an HTMLImageElement or null if failed
 */
export async function loadImageFromPresignedUrl(presignedUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!presignedUrl) {
      resolve(null);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      console.log(`Successfully loaded image from: ${presignedUrl.substring(0, 50)}...`);
      resolve(img);
    };
    img.onerror = (error) => {
      console.error(`Failed to load image from: ${presignedUrl.substring(0, 50)}...`, error);
      resolve(null);
    };
    img.src = presignedUrl;
  });
}

/**
 * Process image URI: extract S3 key, get presigned URL, and load image data
 * @param imageUri The image URI to process
 * @returns Promise that resolves to { s3Key, presignedUrl, imageData } or null values if failed
 */
export async function processImageUri(imageUri: string): Promise<{
  s3Key: string;
  presignedUrl: string | null;
  imageData: HTMLImageElement | null;
}> {
  // Extract S3 key
  const s3Key = extractS3KeyFromImageUri(imageUri);
  
  if (!s3Key) {
    return {
      s3Key: '',
      presignedUrl: null,
      imageData: null
    };
  }
  
  try {
    // Get presigned URL
    const { presignedUrl } = await getPresignedUrl(s3Key);
    
    // Load image data
    const imageData = await loadImageFromPresignedUrl(presignedUrl);
    
    return {
      s3Key,
      presignedUrl,
      imageData
    };
  } catch (error) {
    console.error(`Error processing image URI: ${imageUri}`, error);
    return {
      s3Key,
      presignedUrl: null,
      imageData: null
    };
  }
} 